import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import RoomAttachmentsModal from './RoomAttachmentsModal';
import ItemPhotosModal from './ItemPhotosModal';

const BACKEND = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

/**
 * EditableExportSheet — persisted, floor-grouped, editable export sheet.
 * Works for any sheetKey (customer / electrician / load-in / movers / plumber /
 * tile / wallpaper / art-hanging).
 *
 * Edits are sheet-local: the underlying FF&E item is never rewritten, so two
 * trades can describe the same product differently. Printing renders each
 * sheet's own branded layout using exactly what was edited here.
 */

// Per-sheet "fill in on site" field, mirroring _TRADE_SHEET_CONFIG on the server.
// Every paint sheen, offered as a dropdown so nobody has to remember/spell them.
const SHEEN_OPTIONS = ['Flat', 'Matte', 'Eggshell', 'Satin', 'Semi-Gloss', 'Gloss', 'High-Gloss'];

const FILL_FIELD = {
  electrician: { key: 'bulbs_per_fixture', label: 'Bulbs/Fix', width: 'w-20', placeholder: '#' },
  plumber: { key: 'trade_note', label: 'Rough-in', width: 'w-28', placeholder: 'valve' },
  tile: { key: 'trade_note', label: 'Sq Ft / Grout', width: 'w-28', placeholder: 'sq ft' },
  wallpaper: { key: 'trade_note', label: 'Rolls', width: 'w-24', placeholder: 'rolls' },
  'art-hanging': { key: 'trade_note', label: 'Center Ht', width: 'w-24', placeholder: "60\"" },
  // Requested 25 Aug. Sheen is the one thing a colour alone never tells a
  // painter, so that is what the painter's sheet asks for. Now a dropdown of
  // every sheen, blank until one is chosen.
  painter: { key: 'sheen', label: 'Sheen', width: 'w-32', type: 'select', options: SHEEN_OPTIONS },
  cabinets: { key: 'trade_note', label: 'Door / Drawer Style', width: 'w-36', placeholder: 'shaker' },
};

const OV_FIELDS = [
  'size_note', 'install_notes', 'bulbs_per_fixture', 'trade_note', 'order_index',
  'cabinet_type', 'wood_species',
  // Sheet-local, exactly as asked: never written back to the FF&E or Checklist.
  'sheen', 'surfaces',
  // Core product fields edited on the sheet are sheet-local too (user rule).
  'name', 'vendor', 'sku', 'size', 'finish_color', 'quantity', 'remarks', 'status',
];

// Order statuses for the bulk-edit dropdown (mirrors the FF&E grid list).
const STATUS_OPTIONS = ['TO BE SELECTED', 'RESEARCHING', 'PENDING APPROVAL', 'APPROVED',
  'ORDERED', 'PICKED', 'CONFIRMED', 'IN PRODUCTION', 'SHIPPED', 'IN TRANSIT',
  'OUT FOR DELIVERY', 'DELIVERED TO RECEIVER', 'DELIVERED TO JOB SITE', 'RECEIVED',
  'READY FOR INSTALL', 'INSTALLING', 'INSTALLED', 'ON HOLD', 'BACKORDERED', 'DAMAGED',
  'RETURNED', 'CANCELLED'];

// Fields the Bulk Edit bar can set across many selected rows (all sheet-local).
const BULK_FIELDS = [
  { key: 'vendor', label: 'Vendor', type: 'text' },
  { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
  { key: 'quantity', label: 'Quantity', type: 'number' },
  { key: 'size', label: 'Size', type: 'text' },
  { key: 'finish_color', label: 'Finish / Colour', type: 'text' },
];

// Sheets where "which surfaces get this colour" is a sensible question.
const SURFACE_SHEETS = new Set(['painter', 'cabinets']);

// Per-sheet extra spec columns (mirror of the backend _TRADE_SHEET_CONFIG extra_fields).
const EXTRA_FIELDS = {
  cabinets: [
    { key: 'cabinet_type', label: 'Cabinet Type', width: 'w-32', placeholder: 'wood / painted…' },
    { key: 'wood_species', label: 'Wood Species', width: 'w-32', placeholder: 'white oak…' },
  ],
};

/** Inline editable cell — resets when the incoming value changes, saves on blur. */
const EditCell = ({ value, onSave, className, placeholder, type = 'text', testId }) => {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  return (
    <input
      data-testid={testId}
      type={type}
      value={v}
      placeholder={placeholder || ''}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (String(v) !== String(value ?? '')) onSave(v); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      className={className}
    />
  );
};

export default function EditableExportSheet({ projectId, sheetKey = 'customer', sheetLabel = 'Customer Sheet', onClose }) {
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemStates, setItemStates] = useState({});   // { item_id: included(bool) }
  const [overrides, setOverrides] = useState({});      // { item_id: {…sheet-local edits} }
  const [roomOrder, setRoomOrder] = useState([]);      // [room_id…]
  const [expanded, setExpanded] = useState({});        // { item_id: bool }
  const [collapsedRooms, setCollapsedRooms] = useState({}); // { room_id: true = minimised }
  const toggleRoomCollapse = (rid) => setCollapsedRooms(p => ({ ...p, [rid]: !p[rid] }));
  // Bulk Edit — tick multiple rows, then set a field on all at once (sheet-local).
  const [selectedItems, setSelectedItems] = useState({}); // { item_id: true }
  const [bulkField, setBulkField] = useState('vendor');
  const [bulkValue, setBulkValue] = useState('');
  const [lastBulkAction, setLastBulkAction] = useState(null); // undo snapshot

  // Print scope: which categories to show/print, which sections to print, and
  // whether to append the room reference cards.
  const [catFilter, setCatFilter] = useState('all');       // 'all' | category name
  const [selectedRooms, setSelectedRooms] = useState(null); // null = every room
  const [withRoomSheet, setWithRoomSheet] = useState(false);
  // Load-in only: how many photos share a page, and the room whose attachments
  // are open. 4 is what the client asked for; fewer makes each photo bigger.
  const [perPage, setPerPage] = useState(4);
  const [attachRoom, setAttachRoom] = useState(null);   // { id, name }
  const [attachCounts, setAttachCounts] = useState({}); // { room_id: n }
  const isLoadIn = sheetKey === 'load-in';

  const [showAdd, setShowAdd] = useState(false);
  const [addTab, setAddTab] = useState('custom');      // 'custom' | 'ffe' | 'checklist'
  const [addForm, setAddForm] = useState({ room_id: '', category_id: '', name: '', vendor: '', size: '', shape_note: '', image_url: '' });
  const [search, setSearch] = useState('');
  const [checklistItems, setChecklistItems] = useState(null);
  // Requested 4 Sep: "want to be sure to sort all of those items". The picker
  // used to come back in whatever order the rooms happened to be stored in.
  const [pickerSort, setPickerSort] = useState('room');   // 'room' | 'name' | 'vendor'
  const [ffeSaved, setFfeSaved] = useState('');        // '', 'saving', 'saved', 'error'
  const [photoItem, setPhotoItem] = useState(null);    // { item_id, name, photos }
  const autosaveTimer = useRef(null);
  // Presets / templates / auto-fill / room header photos (parity with print sheet)
  const [showPresets, setShowPresets] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetForm, setPresetForm] = useState({ name: '', fill: '', notes: '' });
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [autoFilling, setAutoFilling] = useState(false);
  const roomPhotoInputRef = useRef(null);
  const roomPhotoTargetRef = useRef(null);

  const fill = FILL_FIELD[sheetKey] || null;
  const extraFields = EXTRA_FIELDS[sheetKey] || [];
  const showSurfaces = SURFACE_SHEETS.has(sheetKey);
  // Sent with the sheet so the UI and the printed page share one vocabulary.
  const surfaceOptions = sheet?.surface_options || [];

  // Signature of the last-persisted doc. Set when a sheet loads and after each
  // autosave, so merely OPENING the sheet never fires a (lossy) save.
  const lastSaved = useRef(null);
  const cleanOverrides = (ov) => {
    const cleaned = {};
    Object.entries(ov || {}).forEach(([id, o]) => {
      const kept = {};
      OV_FIELDS.forEach(k => {
        const v = o[k];
        if (v === undefined || v === '') return;
        if (Array.isArray(v) && v.length === 0) return;   // no surfaces ticked
        kept[k] = v;
      });
      if (Object.keys(kept).length) cleaned[id] = kept;
    });
    return cleaned;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/export-sheets/${projectId}/${sheetKey}`);
      const data = await res.json();
      setSheet(data);
      const states = {};
      const ov = {};
      (data.floors || []).forEach(f => (f.rooms || []).forEach(r => (r.categories || []).forEach(c => (c.items || []).forEach(it => {
        states[it.item_id] = it.included;
        // Seed EVERY item's per-sheet overrides, including the drag order_index
        // (the API returns it top-level, not inside it.ov) — otherwise the next
        // autosave would wipe the ordering and any field we didn't read back.
        ov[it.item_id] = {
          size_note: it.size_note || '',
          shape_note: it.shape_note || '',
          ...(it.ov || {}),
          order_index: it.order_index ?? 0,
        };
      }))));
      const ro = data.room_order && data.room_order.length
        ? data.room_order
        : (data.floors || []).flatMap(f => (f.rooms || []).map(r => r.room_id));
      setItemStates(states);
      setOverrides(ov);
      setRoomOrder(ro);
      lastSaved.current = JSON.stringify({ is: states, ov: cleanOverrides(ov), ro });
    } catch (e) {
      console.error('Failed to load export sheet:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (projectId) load(); /* eslint-disable-next-line */ }, [projectId, sheetKey]);

  // Live refresh: reload the sheet when this project's data changed elsewhere
  // (another sheet, the FF&E tab), unless the user is mid-edit in this modal.
  const dataVerRef = useRef(null);
  const ffeSavedRef = useRef('');
  const savingRef = useRef(false);
  useEffect(() => { ffeSavedRef.current = ffeSaved; }, [ffeSaved]);
  useEffect(() => { savingRef.current = saving; }, [saving]);
  useEffect(() => {
    if (!projectId) return undefined;
    const poll = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/projects/${projectId}/data-version`);
        const d = await r.json();
        if (dataVerRef.current === null) { dataVerRef.current = d.v; return; }
        if (d.v !== dataVerRef.current) {
          dataVerRef.current = d.v;
          const el = document.activeElement;
          const editing = el && (el.isContentEditable || /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName || ''));
          if (!editing && ffeSavedRef.current !== 'saving' && savingRef.current !== true) load();
        }
      } catch { /* ignore */ }
    };
    const id = setInterval(poll, 12000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [projectId, sheetKey]);

  /** Attachment counts per room, so each button can show how many are held. */
  const loadAttachCounts = React.useCallback(async (roomIds) => {
    if (!isLoadIn || !roomIds.length) return;
    try {
      const pairs = await Promise.all((roomIds || []).map(async id => {
        const res = await fetch(`${BACKEND}/api/projects/${projectId}/rooms/${id}/attachments`);
        if (!res.ok) return [id, 0];
        const list = await res.json();
        return [id, Array.isArray(list) ? list.length : 0];
      }));
      setAttachCounts(Object.fromEntries(pairs));
    } catch (e) {
      // A failed count is cosmetic — the button still opens.
      console.warn('Could not read attachment counts:', e);
    }
  }, [projectId, isLoadIn]);

  useEffect(() => {
    const ids = (sheet?.floors || []).flatMap(f => (f.rooms || []).map(r => r.room_id));
    loadAttachCounts(ids);
  }, [sheet, loadAttachCounts]);

  // Clear any bulk selection when the category filter changes, so a user never
  // bulk-applies to rows that aren't currently visible.
  useEffect(() => { setSelectedItems({}); }, [catFilter]);

  const toggleInclude = (id) => setItemStates(prev => ({ ...prev, [id]: !prev[id] }));
  const setNote = (id, field, val) => setOverrides(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: val } }));

  // Tick or untick one surface for one row. Kept in the sheet's own
  // overrides, so the FF&E item behind it is untouched.
  const toggleSurface = (id, surface) => setOverrides(prev => {
    const current = (prev[id] || {}).surfaces || [];
    const next = current.includes(surface)
      ? current.filter(x => x !== surface)
      : [...current, surface];
    return { ...prev, [id]: { ...(prev[id] || {}), surfaces: next } };
  });
  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  /** Find the matching base item inside the loaded sheet tree (for undo snapshots). */
  const findSheetItem = (id) => {
    for (const f of (sheet?.floors || []))
      for (const r of (f.rooms || []))
        for (const c of (r.categories || []))
          for (const it of (c.items || []))
            if (it.item_id === id) return it;
    return null;
  };

  /** Merge a patch into the matching base item inside the loaded sheet tree. */
  const patchSheetItem = (itemId, patch) => {
    setSheet(prev => {
      if (!prev) return prev;
      const floors = (prev.floors || []).map(f => ({
        ...f,
        rooms: (f.rooms || []).map(r => ({
          ...r,
          categories: (r.categories || []).map(c => ({
            ...c,
            items: (c.items || []).map(it => (it.item_id === itemId ? { ...it, ...patch } : it)),
          })),
        })),
      }));
      return { ...prev, floors };
    });
  };

  /** Edits on a sheet are SHEET-LOCAL (user rule: the FF&E is the master and is
   * NEVER written to from a sheet — only a new import or the FF&E grid changes
   * it). We store the value as a per-sheet override (autosaved) and reflect it
   * on the sheet immediately. Clearing a field reverts that cell to the FF&E
   * value. */
  const saveSheetOverride = (itemId, field, value) => {
    if (!itemId || String(itemId).startsWith('wh__')) return;
    const val = field === 'quantity' ? (parseInt(value, 10) || 1) : value;
    patchSheetItem(itemId, { [field]: val });   // show it on this sheet now
    setNote(itemId, field, val);                // persist as a sheet-local override
    setFfeSaved('saved');
  };

  /** Remove an item from THIS sheet only (it stays in the FF&E and other
   * sheets). Reversible — it drops into the "items not on this sheet" list. */
  const removeFromSheet = (itemId) => {
    setItemStates(prev => ({ ...prev, [itemId]: false }));
    setFfeSaved('saved');
  };

  // ---- Bulk Edit (all sheet-local; the FF&E master is never touched) ----
  const toggleSelect = (id) => setSelectedItems(prev => {
    const n = { ...prev };
    if (n[id]) delete n[id]; else n[id] = true;
    return n;
  });
  const clearSelection = () => setSelectedItems({});
  const visibleEditableIds = () => {
    const ids = [];
    (sheet?.floors || []).forEach(f => (f.rooms || []).forEach(r => (r.categories || [])
      .filter(c => catFilter === 'all' || c.category_name === catFilter)
      .forEach(c => (c.items || []).forEach(it => {
        if (!it.read_only && !String(it.item_id || '').startsWith('wh__')) ids.push(it.item_id);
      }))));
    return ids;
  };
  const selectAllShown = () => {
    const map = {};
    visibleEditableIds().forEach(id => { map[id] = true; });
    setSelectedItems(map);
  };
  const applyBulk = () => {
    const ids = Object.keys(selectedItems).filter(id => !String(id).startsWith('wh__'));
    if (!ids.length) return;
    const val = bulkField === 'quantity' ? (parseInt(bulkValue, 10) || 1) : bulkValue;
    // Snapshot for one-click undo: previous override + displayed value per row.
    const snap = { type: 'edit', field: bulkField, ids: [...ids], prevOv: {}, prevDisp: {} };
    ids.forEach(id => {
      snap.prevOv[id] = overrides[id] ? overrides[id][bulkField] : undefined;
      const it = findSheetItem(id);
      snap.prevDisp[id] = it ? it[bulkField] : undefined;
    });
    ids.forEach(id => patchSheetItem(id, { [bulkField]: val }));
    setOverrides(prev => {
      const n = { ...prev };
      ids.forEach(id => { n[id] = { ...(n[id] || {}), [bulkField]: val }; });
      return n;
    });
    setLastBulkAction(snap);
    setFfeSaved('saved');
  };
  const bulkRemove = () => {
    const ids = Object.keys(selectedItems);
    if (!ids.length) return;
    const snap = { type: 'remove', ids: [...ids], prevStates: {} };
    ids.forEach(id => { snap.prevStates[id] = itemStates[id]; });
    setItemStates(prev => {
      const n = { ...prev };
      ids.forEach(id => { n[id] = false; });
      return n;
    });
    setLastBulkAction(snap);
    setFfeSaved('saved');
    clearSelection();
  };
  /** One-click undo of the last bulk action (edit or remove). Restores the
   * previous sheet-local override / include state — the FF&E is never touched. */
  const undoBulk = () => {
    const snap = lastBulkAction;
    if (!snap) return;
    if (snap.type === 'edit') {
      setOverrides(prev => {
        const n = { ...prev };
        snap.ids.forEach(id => {
          const cur = { ...(n[id] || {}) };
          if (snap.prevOv[id] === undefined) delete cur[snap.field];
          else cur[snap.field] = snap.prevOv[id];
          n[id] = cur;
        });
        return n;
      });
      snap.ids.forEach(id => { patchSheetItem(id, { [snap.field]: snap.prevDisp[id] }); });
    } else if (snap.type === 'remove') {
      setItemStates(prev => {
        const n = { ...prev };
        snap.ids.forEach(id => {
          if (snap.prevStates[id] === undefined) delete n[id];
          else n[id] = snap.prevStates[id];
        });
        return n;
      });
    }
    setLastBulkAction(null);
    setFfeSaved('saved');
  };

  /** A photo was set as the item's main image in the photos modal — reflect it
   * on the sheet thumbnail right away (the master was already updated). */
  const onMainPhoto = (itemId, src) => {
    patchSheetItem(itemId, { image_url: src });
    setFfeSaved('saved');
  };

  /** Rename a room / location — writes to the FF&E room so it syncs everywhere. */
  const saveRoomName = async (roomId, name) => {
    const nm = (name || '').trim();
    if (!roomId || !nm || String(roomId).startsWith('wh__')) return;
    setSheet(prev => prev && ({
      ...prev,
      floors: (prev.floors || []).map(f => ({
        ...f,
        rooms: (f.rooms || []).map(r => (r.room_id === roomId ? { ...r, room_name: nm } : r)),
      })),
    }));
    setFfeSaved('saving');
    try {
      const res = await fetch(`${BACKEND}/api/rooms/${roomId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nm }),
      });
      setFfeSaved(res.ok ? 'saved' : 'error');
    } catch {
      setFfeSaved('error');
    }
  };

  // ---- presets ----
  const loadPresets = async () => {
    try { const r = await fetch(`${BACKEND}/api/sheet-presets/${sheetKey}`); const d = await r.json(); setPresets(d.presets || []); } catch { /* ignore */ }
  };
  const savePreset = async () => {
    if (!presetForm.name.trim()) return;
    await fetch(`${BACKEND}/api/sheet-presets/${sheetKey}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: presetForm.name, fill_key: fill?.key || 'trade_note', fill_label: fill?.label || '', fill_value: presetForm.fill, install_notes: presetForm.notes }),
    });
    setPresetForm({ name: '', fill: '', notes: '' }); loadPresets();
  };
  const applyPreset = async (p) => {
    await fetch(`${BACKEND}/api/export-sheets/${projectId}/${sheetKey}/apply-preset`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fill_key: p.fill_key || fill?.key || 'trade_note', fill_value: p.fill_value, install_notes: p.install_notes }),
    });
    setShowPresets(false); load();
  };
  const deletePreset = async (id) => { await fetch(`${BACKEND}/api/sheet-presets/${sheetKey}/${id}`, { method: 'DELETE' }); loadPresets(); };

  // ---- templates ----
  const loadTemplates = async () => {
    try { const r = await fetch(`${BACKEND}/api/sheet-templates/${sheetKey}`); const d = await r.json(); setTemplates(d.templates || []); } catch { /* ignore */ }
  };
  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    await fetch(`${BACKEND}/api/export-sheets/${projectId}/${sheetKey}/save-template`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: templateName }),
    });
    setTemplateName(''); loadTemplates();
  };
  const applyTemplate = async (t) => {
    await fetch(`${BACKEND}/api/export-sheets/${projectId}/${sheetKey}/apply-template`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ template_id: t.id }),
    });
    setShowTemplates(false); load();
  };
  const deleteTemplate = async (id) => { await fetch(`${BACKEND}/api/sheet-templates/${sheetKey}/${id}`, { method: 'DELETE' }); loadTemplates(); };
  const setDefaultTemplate = async (id) => { await fetch(`${BACKEND}/api/sheet-templates/${sheetKey}/${id}/set-default`, { method: 'POST' }); loadTemplates(); };

  // ---- auto-fill photos from vendor links ----
  const autoFillPhotos = async () => {
    setAutoFilling(true);
    try {
      const r = await fetch(`${BACKEND}/api/export-sheets/${projectId}/${sheetKey}/auto-fill-photos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const d = await r.json();
      if (d.filled) load();
    } finally { setAutoFilling(false); }
  };

  // ---- room header photos ----
  const patchRoomPhotos = (roomId, photos) => {
    setSheet(prev => prev && ({
      ...prev,
      floors: (prev.floors || []).map(f => ({ ...f, rooms: (f.rooms || []).map(r => (r.room_id === roomId ? { ...r, header_photos: photos } : r)) })),
    }));
  };
  const addRoomPhotos = async (roomId, files) => {
    if (!roomId || !files || !files.length) return;
    const fd = new FormData();
    for (let i = 0; i < files.length; i++) fd.append('files', files[i]);
    setFfeSaved('saving');
    try {
      const r = await fetch(`${BACKEND}/api/rooms/${roomId}/header-photos`, { method: 'POST', body: fd });
      const d = await r.json();
      if (r.ok) { patchRoomPhotos(roomId, d.header_photos || []); setFfeSaved('saved'); } else setFfeSaved('error');
    } catch { setFfeSaved('error'); }
  };
  const deleteRoomPhoto = async (roomId, idx) => {
    try { const r = await fetch(`${BACKEND}/api/rooms/${roomId}/header-photos/${idx}`, { method: 'DELETE' }); const d = await r.json(); if (r.ok) patchRoomPhotos(roomId, d.header_photos || []); } catch { /* ignore */ }
  };
  const pickRoomPhotos = (roomId) => { roomPhotoTargetRef.current = roomId; setTimeout(() => roomPhotoInputRef.current?.click(), 0); };

  // persists current values without re-binding the timer on every keystroke.
  const stateRef = useRef({ itemStates, overrides, roomOrder });
  useEffect(() => { stateRef.current = { itemStates, overrides, roomOrder }; }, [itemStates, overrides, roomOrder]);

  const persistDoc = async () => {
    const { itemStates: is, overrides: ov, roomOrder: ro } = stateRef.current;
    try {
      await fetch(`${BACKEND}/api/export-sheets/${projectId}/${sheetKey}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_states: is, overrides: cleanOverrides(ov), room_order: ro }),
      });
    } catch (e) {
      console.warn('sheet autosave failed', e);
    }
  };

  // Autosave include toggles / reorder / trade notes shortly after any change.
  // Compares a signature against the last-saved one so opening a sheet (or a
  // no-op re-render) never triggers a write.
  useEffect(() => {
    const sig = JSON.stringify({ is: itemStates, ov: cleanOverrides(overrides), ro: roomOrder });
    if (lastSaved.current === null || sig === lastSaved.current) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { lastSaved.current = sig; persistDoc(); }, 700);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line
  }, [itemStates, overrides, roomOrder]);

  const reorderArr = (list, from, to) => {
    const a = [...list];
    const [moved] = a.splice(from, 1);
    a.splice(to, 0, moved);
    return a;
  };

  /** Drag to reorder — items within a category, or whole rooms within a floor. */
  const handleDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === 'ITEM') {
      const catId = source.droppableId.replace('items-', '');
      let catItems = null;
      (sheet?.floors || []).forEach(f => (f.rooms || []).forEach(r => (r.categories || []).forEach(c => {
        if (c.category_id === catId) catItems = c.items || [];
      })));
      if (!catItems) return;
      const reordered = reorderArr(catItems, source.index, destination.index);
      const ids = (reordered || []).map(i => i.item_id);
      // Reorder the rendered tree so the move is visible immediately…
      setSheet(prev => prev && ({
        ...prev,
        floors: (prev.floors || []).map(f => ({
          ...f,
          rooms: (f.rooms || []).map(r => ({
            ...r,
            categories: (r.categories || []).map(c => (c.category_id === catId ? { ...c, items: reordered } : c)),
          })),
        })),
      }));
      // …and persist the new order_index per item.
      setOverrides(prev => {
        const n = { ...prev };
        (ids || []).forEach((id, i) => { n[id] = { ...(n[id] || {}), order_index: i }; });
        return n;
      });
      return;
    }

    if (type === 'ROOM') {
      const floorName = source.droppableId.replace('rooms-', '');
      const floor = (sheet?.floors || []).find(f => String(f.floor) === floorName);
      if (!floor) return;
      const reorderedRooms = reorderArr(floor.rooms || [], source.index, destination.index);
      const full = (sheet?.floors || []).flatMap(f =>
        String(f.floor) === floorName ? (reorderedRooms || []).map(r => r.room_id) : (f.rooms || []).map(r => r.room_id));
      setSheet(prev => prev && ({
        ...prev,
        floors: (prev.floors || []).map(f => (String(f.floor) === floorName ? { ...f, rooms: reorderedRooms } : f)),
      }));
      setRoomOrder(full);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Drop empty override objects so the saved doc stays small.
      const cleaned = {};
      Object.entries(overrides).forEach(([id, o]) => {
        const kept = {};
        OV_FIELDS.forEach(k => {
        const v = o[k];
        if (v === undefined || v === '') return;
        if (Array.isArray(v) && v.length === 0) return;   // no surfaces ticked
        kept[k] = v;
      });
        if (Object.keys(kept).length) cleaned[id] = kept;
      });
      const res = await fetch(`${BACKEND}/api/export-sheets/${projectId}/${sheetKey}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_states: itemStates, overrides: cleaned, room_order: roomOrder }),
      });
      if (!res.ok) throw new Error('save failed');
      alert('✅ Sheet saved');
      await load();
    } catch (e) {
      alert('❌ Failed to save sheet');
    } finally {
      setSaving(false);
    }
  };

  const submitCustom = async () => {
    if (!addForm.room_id || !addForm.category_id || !addForm.name.trim()) {
      alert('Room, category and item name are required.');
      return;
    }
    try {
      const res = await fetch(`${BACKEND}/api/export-sheets/${projectId}/${sheetKey}/custom-item`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      if (!res.ok) throw new Error('add failed');
      closeAdd();
      await load();
    } catch (e) {
      alert('❌ Failed to add item');
    }
  };

  const closeAdd = () => {
    setShowAdd(false);
    setAddTab('custom');
    setSearch('');
    setAddForm({ room_id: '', category_id: '', name: '', vendor: '', size: '', shape_note: '', image_url: '' });
  };

  const onImage = (file) => {
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => setAddForm(f => ({ ...f, image_url: fr.result }));
    fr.readAsDataURL(file);
  };

  const loadChecklist = async () => {
    if (checklistItems !== null) return;
    try {
      const res = await fetch(`${BACKEND}/api/projects/${projectId}?sheet_type=checklist`);
      const proj = await res.json();
      const flat = [];
      (proj.rooms || []).forEach(r => (r.categories || []).forEach(c => (c.subcategories || []).forEach(s => (s.items || []).forEach(it => {
        flat.push({ name: it.name || '', vendor: it.vendor || '', size: it.size || '', room_name: r.name || '', category_name: c.name || '',
                    // The product photo only -- never the finish swatch.
                    image_url: it.image_url || it.photo_url || '' });
      }))));
      setChecklistItems(flat);
    } catch (e) {
      setChecklistItems([]);
    }
  };

  const openAdd = (tab) => {
    setAddTab(tab);
    setShowAdd(true);
    if (tab === 'checklist') loadChecklist();
  };

  // Pre-fill the custom form from a picked Checklist item (user then picks room+category)
  const pickChecklistItem = (ci) => {
    setAddForm(f => ({ ...f, name: ci.name, vendor: ci.vendor, size: ci.size }));
    setAddTab('custom');
  };

  /** Open the branded print view, narrowed to the given rooms/categories. */
  const printScoped = ({ rooms, categories } = {}) => {
    const qs = new URLSearchParams();
    if (rooms && rooms.length) qs.set('rooms', rooms.join(','));
    if (categories && categories.length) qs.set('categories', categories.join(','));
    if (withRoomSheet) qs.set('room_sheet', '1');
    if (isLoadIn) qs.set('per_page', String(perPage));
    const q = qs.toString();
    window.open(`${BACKEND}/api/export-sheets/${projectId}/${sheetKey}/print${q ? `?${q}` : ''}`, '_blank');
  };

  const activeCats = catFilter === 'all' ? [] : [catFilter];

  /** Print everything currently in scope (all sections, or just the ticked ones). */
  const printSheet = () => printScoped({
    rooms: selectedRooms === null ? [] : selectedRooms,
    categories: activeCats,
  });

  // Every category name on the sheet, in sheet order — drives the filter chips.
  const allCategories = [];
  (sheet?.floors || []).forEach(f => (f.rooms || []).forEach(r => (r.categories || []).forEach(c => {
    if (c.category_name && !allCategories.includes(c.category_name)) allCategories.push(c.category_name);
  })));

  const allRoomIds = (sheet?.floors || []).flatMap(f => (f.rooms || []).map(r => r.room_id));
  const roomChecked = (id) => selectedRooms === null || selectedRooms.includes(id);
  const toggleRoomSelected = (id) => setSelectedRooms(prev => {
    const cur = prev === null ? allRoomIds : prev;
    return cur.includes(id) ? (cur || []).filter(x => x !== id) : [...cur, id];
  });
  const allSectionsSelected = selectedRooms === null || selectedRooms.length === allRoomIds.length;

  /** Tick / untick every item in one room, honouring the category filter. */
  const setRoomItems = (room, on) => {
    const next = {};
    (room.categories || [])
      .filter(c => catFilter === 'all' || c.category_name === catFilter)
      .forEach(c => (c.items || []).forEach(it => { next[it.item_id] = on; }));
    setItemStates(prev => ({ ...prev, ...next }));
  };

  const pickerRooms = sheet?.rooms_for_picker || [];
  const pickerCats = pickerRooms.find(r => r.room_id === addForm.room_id)?.categories || [];

  // Flat list of every FF&E item on the sheet (for the "From FF&E" picker)
  const allItems = [];
  (sheet?.floors || []).forEach(f => (f.rooms || []).forEach(r => (r.categories || []).forEach(c => (c.items || []).forEach(it => {
    allItems.push({ ...it, floor: f.floor, room_name: r.room_name, category_name: c.category_name });
  }))));
  const q = search.trim().toLowerCase();

  // Sort comparators for the pickers. 'room' keeps a room's items together,
  // which is how the sheet itself reads; the other two are for hunting a
  // specific product by name or by who supplies it.
  const byText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });
  const sortPicker = (list) => {
    const out = [...list];
    if (pickerSort === 'name') out.sort((a, b) => byText(a.name, b.name));
    else if (pickerSort === 'vendor') out.sort((a, b) => byText(a.vendor, b.vendor) || byText(a.name, b.name));
    else out.sort((a, b) => byText(a.room_name, b.room_name)
                         || byText(a.category_name, b.category_name)
                         || byText(a.name, b.name));
    return out;
  };

  const ffeCandidates = sortPicker(allItems.filter(it => !itemStates[it.item_id] && (!q || it.name.toLowerCase().includes(q) || (it.vendor || '').toLowerCase().includes(q))));
  const checklistCandidates = sortPicker((checklistItems || []).filter(ci => !q || ci.name.toLowerCase().includes(q) || ci.vendor.toLowerCase().includes(q)));

  // Thumbnail for a picker row. Requested 4 Sep: "it needs to have the picture
  // next to the item, not JUST the item name." A missing photo shows an empty
  // frame rather than nothing, so the rows stay aligned.
  const pickerThumb = (src, alt) => (
    src
      ? <img src={src} alt={alt || ''} className="w-10 h-10 object-cover rounded flex-shrink-0 bg-black/40" />
      : <div className="w-10 h-10 rounded flex-shrink-0 bg-black/40 border border-[#D4A574]/20 flex items-center justify-center text-[9px] text-gray-500">no&nbsp;img</div>
  );

  const sortControl = (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-xs text-gray-500">Sort</span>
      <select data-testid="picker-sort" value={pickerSort} onChange={e => setPickerSort(e.target.value)}
        className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-xs">
        <option value="room">Room &amp; category</option>
        <option value="name">Item name (A–Z)</option>
        <option value="vendor">Vendor</option>
      </select>
    </div>
  );

  const inputCls = 'w-full bg-black/40 border border-[#D4A574]/30 rounded px-1 py-0.5 text-white text-xs';
  const dragHandleCls = 'cursor-grab active:cursor-grabbing text-[#B49B7E] hover:text-[#D4A574] select-none px-1 leading-none';
  const selectedCount = Object.keys(selectedItems).length;
  const bulkFieldCfg = BULK_FIELDS.find(f => f.key === bulkField) || BULK_FIELDS[0];

  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto p-4">
      <div className="max-w-7xl mx-auto bg-[#0F172A] border border-[#D4A574]/40 rounded-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-[#D4A574]/30 bg-[#0F172A] rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-[#D4A574]">📝 {sheetLabel} — Editable</h2>
            <p className="text-[11px] text-[#B49B7E]">Edits here stay on <b>THIS sheet only</b> — the FF&E master is never changed from a sheet (only a new import or the FF&E grid changes it). Clearing a field reverts it to the FF&E value. The 🗑 removes an item from this sheet (it stays in the FF&E). Tick rows to <b>Bulk Edit</b> vendor, status, qty, size or finish at once.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span data-testid="ffe-save-state" className={`text-[11px] ${ffeSaved === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>
              {ffeSaved === 'saving' ? 'Saving…' : ffeSaved === 'saved' ? 'Saved ✓' : ffeSaved === 'error' ? 'Save failed' : ''}
            </span>
            <button data-testid="export-sheet-add-item" onClick={() => openAdd('custom')} className="px-3 py-2 bg-amber-700 hover:bg-amber-600 text-white rounded text-sm font-medium">✚ Add Item</button>
            {!isLoadIn && (
              <>
                <button data-testid="export-sheet-presets-btn" onClick={() => { setShowPresets(true); loadPresets(); }} className="px-3 py-2 bg-purple-800 hover:bg-purple-700 text-white rounded text-sm font-medium">★ Presets</button>
                <button data-testid="export-sheet-templates-btn" onClick={() => { setShowTemplates(true); loadTemplates(); }} className="px-3 py-2 bg-teal-800 hover:bg-teal-700 text-white rounded text-sm font-medium">📐 Templates</button>
                <button data-testid="export-sheet-autofill-btn" onClick={autoFillPhotos} disabled={autoFilling} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm font-medium disabled:opacity-50">{autoFilling ? 'Filling…' : '🖼️ Auto-fill photos'}</button>
              </>
            )}
            <button data-testid="export-sheet-save" onClick={save} disabled={saving} className="px-3 py-2 bg-[#D4A574] hover:bg-[#E2B989] text-black rounded text-sm font-bold disabled:opacity-50">{saving ? 'Saving…' : '💾 Save'}</button>
            <button data-testid="export-sheet-print" onClick={printSheet} title="Open a full print-ready preview (print from there)" className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm font-medium">👁️ Preview</button>
            <button data-testid="export-sheet-close" onClick={onClose} className="px-3 py-2 text-gray-300 hover:text-white text-sm">✕ Close</button>
          </div>
        </div>

        {/* Print scope bar: show one category or all, choose which sections print. */}
        {!loading && sheet && (sheet.floors || []).length > 0 && (
          <div className="px-6 py-3 border-b border-[#D4A574]/20 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-[#B49B7E] mr-1">Categories</span>
              <button data-testid="cat-chip-all" onClick={() => setCatFilter('all')}
                className={`px-2.5 py-1 rounded-full text-xs ${catFilter === 'all' ? 'bg-[#D4A574] text-black font-bold' : 'bg-slate-700 text-gray-200 hover:bg-slate-600'}`}>
                Show all
              </button>
              {allCategories.map(cn => (
                <button key={cn} data-testid={`cat-chip-${cn}`} onClick={() => setCatFilter(cn)}
                  className={`px-2.5 py-1 rounded-full text-xs ${catFilter === cn ? 'bg-[#D4A574] text-black font-bold' : 'bg-slate-700 text-gray-200 hover:bg-slate-600'}`}>
                  {cn}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 flex-wrap text-xs text-[#D4C5A9]">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" data-testid="select-all-sections" checked={allSectionsSelected}
                  onChange={() => setSelectedRooms(allSectionsSelected ? [] : null)}
                  className="w-3.5 h-3.5 accent-[#D4A574]" />
                Select all sections
              </label>
              <span className="text-[#B49B7E]">
                {selectedRooms === null ? allRoomIds.length : selectedRooms.length} of {allRoomIds.length} section(s) will print
              </span>
              {isLoadIn && (
                <label className="flex items-center gap-1.5" title="Fewer per page means bigger photos">
                  Photos per page
                  <select value={perPage} onChange={e => setPerPage(Number(e.target.value))}
                    data-testid="load-in-per-page"
                    className="bg-black/40 border border-[#D4A574]/30 rounded px-1 py-0.5 text-white text-xs">
                    {[1, 2, 4, 6, 8].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              )}
              <label className="flex items-center gap-1.5 cursor-pointer" title="Adds a page with each room's plan, dimensions and finishes">
                <input type="checkbox" data-testid="with-room-sheet" checked={withRoomSheet}
                  onChange={e => setWithRoomSheet(e.target.checked)}
                  className="w-3.5 h-3.5 accent-[#D4A574]" />
                Include room reference (plan + finishes)
              </label>
              <span className="w-px h-4 bg-[#B49B7E]/30" />
              <button data-testid="select-all-shown-btn" onClick={selectAllShown}
                className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-gray-200 text-xs">☑ Select all shown</button>
              {selectedCount > 0 && (
                <button data-testid="bulk-clear-selection" onClick={clearSelection}
                  className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-gray-300 text-xs">✕ Clear ({selectedCount})</button>
              )}
              <span className="w-px h-4 bg-[#B49B7E]/30" />
              <button data-testid="collapse-all-rooms"
                onClick={() => setCollapsedRooms(Object.fromEntries(allRoomIds.map(id => [id, true])))}
                title="Minimise every room so you can scroll the list fast"
                className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-gray-200 text-xs">⊟ Collapse all rooms</button>
              <button data-testid="expand-all-rooms" onClick={() => setCollapsedRooms({})}
                className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-gray-200 text-xs">⊞ Expand all</button>
            </div>
          </div>
        )}

        <div className="p-6">
          {loading ? (
            <p className="text-center text-[#D4C5A9] py-12">Loading sheet…</p>
          ) : !sheet || (sheet.floors || []).length === 0 ? (
            <p className="text-center text-gray-400 py-12">No FF&E data yet. Add items in the FF&E tab first.</p>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
            {(sheet.floors || []).map(floor => (
              <div key={floor.floor} className="mb-6">
                <div className="px-3 py-2 rounded font-bold text-[#D4A574]" style={{ background: 'linear-gradient(90deg,#2a2218,#3d3020,#2a2218)' }}>🏢 {floor.floor}</div>
                <Droppable droppableId={`rooms-${floor.floor}`} type="ROOM">
                  {(roomsDrop) => (
                    <div ref={roomsDrop.innerRef} {...roomsDrop.droppableProps}>
                    {(floor.rooms || []).map((room, roomIdx) => (
                      <Draggable key={room.room_id} draggableId={`room-${room.room_id}`} index={roomIdx}>
                        {(roomDrag) => (
                          <div ref={roomDrag.innerRef} {...roomDrag.draggableProps} className="mt-3 ml-2">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span {...roomDrag.dragHandleProps} title="Drag to reorder room"
                                data-testid={`room-drag-${room.room_id}`} className={`${dragHandleCls} text-base`}>⋮⋮</span>
                              <button onClick={() => toggleRoomCollapse(room.room_id)}
                                data-testid={`room-collapse-${room.room_id}`}
                                title={collapsedRooms[room.room_id] ? 'Expand this room' : 'Minimise this room'}
                                className="text-[#D4A574] hover:text-[#E2B989] text-base px-1 leading-none select-none">
                                {collapsedRooms[room.room_id] ? '▶' : '▼'}
                              </button>
                              <input type="checkbox" title="Include this section when printing"
                                checked={roomChecked(room.room_id)} onChange={() => toggleRoomSelected(room.room_id)}
                                data-testid={`section-pick-${room.room_id}`} className="w-3.5 h-3.5 accent-[#D4A574] mr-1" />
                              {String(room.room_id || '').startsWith('wh__')
                                ? <span className="text-[#D4C5A9] font-semibold text-lg">🏠 {room.room_name}</span>
                                : (
                                  <span className="flex items-center gap-1 text-[#D4C5A9] font-semibold text-lg">
                                    🚪
                                    <EditCell testId={`edit-room-${room.room_id}`} value={room.room_name}
                                      onSave={(v) => saveRoomName(room.room_id, v)}
                                      placeholder="Room name"
                                      className="bg-black/40 border border-[#D4A574]/30 rounded px-2 py-0.5 text-[#D4C5A9] text-lg font-semibold min-w-[160px]" />
                                  </span>
                                )}
                              <span className="text-[11px] text-[#B49B7E] ml-1" data-testid={`room-count-${room.room_id}`}>
                                ({(room.categories || []).reduce((a, c) => a + (c.items || []).length, 0)} items)
                              </span>
                              <button onClick={() => printScoped({ rooms: [room.room_id], categories: activeCats })}
                                data-testid={`print-section-${room.room_id}`}
                                title={`Preview just ${room.room_name} (print from there)`}
                                className="ml-2 px-2 py-0.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs">👁️ Preview section</button>
                              <button onClick={() => setRoomItems(room, true)} data-testid={`room-all-${room.room_id}`}
                                title="Tick every item in this room"
                                className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-[#D4C5A9] rounded text-xs">✓ All</button>
                              <button onClick={() => setRoomItems(room, false)} data-testid={`room-none-${room.room_id}`}
                                title="Untick every item in this room"
                                className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-[#D4C5A9] rounded text-xs">✗ None</button>
                              {isLoadIn && (
                                <button onClick={() => setAttachRoom({ id: room.room_id, name: room.room_name })}
                                  data-testid={`attachments-${room.room_id}`}
                                  title="Floor plans and example pictures for this room"
                                  className="px-2 py-0.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs">
                                  📎 Attachments{attachCounts[room.room_id] ? ` (${attachCounts[room.room_id]})` : ''}
                                </button>
                              )}
                            </div>
                            {!collapsedRooms[room.room_id] && !String(room.room_id || '').startsWith('wh__') && (
                              <div className="ml-6 mt-2 mb-1" data-testid={`room-photos-${room.room_id}`}>
                                {(room.header_photos || []).length > 0 && (
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {(room.header_photos || []).map((p, i) => (
                                      <div key={i} className="relative">
                                        <img src={p} alt="" className="h-24 w-auto rounded border-2 border-black object-cover" />
                                        <button onClick={() => deleteRoomPhoto(room.room_id, i)} data-testid={`room-photo-del-${room.room_id}-${i}`}
                                          className="absolute -top-2 -right-2 bg-red-700 hover:bg-red-600 text-white rounded-full w-5 h-5 text-xs leading-none">×</button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <button onClick={() => pickRoomPhotos(room.room_id)} data-testid={`room-photo-add-${room.room_id}`}
                                  className="px-2 py-1 bg-[#6b5745] hover:bg-[#7d6552] text-white rounded text-xs font-medium">🖼️ Add room header photos</button>
                              </div>
                            )}
                            {!collapsedRooms[room.room_id] && (room.categories || [])
                              .filter(cat => catFilter === 'all' || cat.category_name === catFilter)
                              .map(cat => (
                              <div key={cat.category_id} className="mt-2 ml-3">
                                <div className="font-bold text-sm uppercase tracking-wide" style={{ color: '#C08A5A' }}>{cat.category_name}</div>
                                <Droppable droppableId={`items-${cat.category_id}`} type="ITEM">
                                  {(itemsDrop) => (
                                    <table className="w-full mt-1 text-sm" ref={itemsDrop.innerRef} {...itemsDrop.droppableProps}>
                                      <thead>
                                        <tr className="text-gray-400 text-xs">
                                          <th className="w-10 p-1 text-[9px]">Sel</th>
                                          <th className="w-10 p-1">On</th><th className="w-14 p-1">Img</th>
                                          <th className="text-left p-1">Item</th><th className="text-left p-1">Vendor</th>
                                          <th className="text-left p-1">Size</th>
                                          {fill && <th className="text-left p-1">{fill.label}</th>}
                                          {showSurfaces && <th className="text-left p-1">Areas</th>}
                                          {extraFields.map(ef => <th key={ef.key} className="text-left p-1">{ef.label}</th>)}
                                          <th className="text-left p-1">Installation Notes</th>
                                          <th className="w-12 p-1">More</th>
                                        </tr>
                                      </thead>
                                      {(cat.items || []).map((it, idx) => {
                                        const isIn = !!itemStates[it.item_id];
                                        const ov = overrides[it.item_id] || {};
                                        const open = !!expanded[it.item_id];
                                        const colSpan = 8 + (fill ? 1 : 0) + extraFields.length;
                                        const ro = !!it.read_only || String(it.item_id || '').startsWith('wh__');
                                        return (
                                          <Draggable key={it.item_id} draggableId={`item-${it.item_id}`} index={idx}>
                                            {(itemDrag) => (
                                              <tbody ref={itemDrag.innerRef} {...itemDrag.draggableProps}>
                                                <tr className={`border-b border-[#B49B7E]/10 ${isIn ? '' : 'opacity-40'}`}>
                                                  <td className="text-center p-1">
                                                    <span {...itemDrag.dragHandleProps} title="Drag to reorder" data-testid={`item-drag-${it.item_id}`} className={dragHandleCls}>⋮⋮</span>
                                                    {!ro && (
                                                      <input type="checkbox" title="Select for bulk edit" data-testid={`bulk-select-${it.item_id}`}
                                                        checked={!!selectedItems[it.item_id]} onChange={() => toggleSelect(it.item_id)}
                                                        className="w-3.5 h-3.5 accent-[#C08A5A] ml-0.5 align-middle" />
                                                    )}
                                                  </td>
                                                  <td className="text-center p-1"><input type="checkbox" data-testid={`include-${it.item_id}`} checked={isIn} onChange={() => toggleInclude(it.item_id)} className="w-4 h-4 accent-[#D4A574]" /></td>
                                                  <td className="p-1">
                                                    {ro ? (
                                                      it.image_url
                                                        ? <img src={it.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                                                        : <div className="w-10 h-10 bg-black/40 rounded flex items-center justify-center text-[#B49B7E]">🏠</div>
                                                    ) : (
                                                    <button type="button" data-testid={`photos-btn-${it.item_id}`} onClick={() => setPhotoItem(it)} title="View / add photos" className="relative block">
                                                      {it.image_url
                                                        ? <img src={it.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                                                        : <div className="w-10 h-10 bg-black/40 rounded flex items-center justify-center text-[#B49B7E]">📷</div>}
                                                      {(it.photos || []).length > 0 && (
                                                        <span className="absolute -top-1 -right-1 bg-[#D4A574] text-black text-[9px] font-bold rounded-full px-1 leading-tight">{it.photos.length}</span>
                                                      )}
                                                    </button>
                                                    )}
                                                  </td>
                                                  <td className="p-1">{ro ? <span className="text-white text-xs">{it.name}</span> : <EditCell testId={`edit-name-${it.item_id}`} value={it.name} onSave={(v) => saveSheetOverride(it.item_id, 'name', v)} className={`${inputCls} min-w-[120px] text-white`} placeholder="Item name" />}</td>
                                                  <td className="p-1">{ro ? <span className="text-[#D4C5A9] text-xs">{it.vendor || '—'}</span> : <EditCell testId={`edit-vendor-${it.item_id}`} value={it.vendor} onSave={(v) => saveSheetOverride(it.item_id, 'vendor', v)} className={`${inputCls} text-[#D4C5A9]`} placeholder="—" />}</td>
                                                  <td className="p-1">{ro ? <span className="text-[#D4C5A9] text-xs">{it.finish_color || it.size || '—'}</span> : <EditCell testId={`edit-size-${it.item_id}`} value={it.size} onSave={(v) => saveSheetOverride(it.item_id, 'size', v)} className={`${inputCls} text-[#D4C5A9]`} placeholder="—" />}</td>
                                                  {fill && (
                                                    <td className="p-1">
                                                      {fill.type === 'select' ? (
                                                        <select value={ov[fill.key] || ''} onChange={e => setNote(it.item_id, fill.key, e.target.value)}
                                                          className={`${inputCls} ${fill.width}`} data-testid={`fill-${it.item_id}`}>
                                                          <option value="">— {fill.label.toLowerCase()} —</option>
                                                          {(fill.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                                                          {ov[fill.key] && !(fill.options || []).includes(ov[fill.key])
                                                            ? <option value={ov[fill.key]}>{ov[fill.key]}</option> : null}
                                                        </select>
                                                      ) : (
                                                        <input value={ov[fill.key] || ''} onChange={e => setNote(it.item_id, fill.key, e.target.value)}
                                                          placeholder={fill.placeholder} className={`${inputCls} ${fill.width}`}
                                                          data-testid={`fill-${it.item_id}`} />
                                                      )}
                                                    </td>
                                                  )}
                                                  {showSurfaces && (
                                                    <td className="p-1">
                                                      {/* "need to add 'which walls, and ceiling' column that
                                                          can be ticked". Only offered on rows that are a
                                                          surface finish -- a table lamp has a colour but no
                                                          walls, so ticking one would be meaningless. */}
                                                      {it.finish_kind === 'material' ? (
                                                        <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 max-w-[13rem]">
                                                          {surfaceOptions.map(sf => {
                                                            const on = (ov.surfaces || []).includes(sf);
                                                            return (
                                                              <label key={sf} title={sf}
                                                                className={`text-[10px] px-1 rounded cursor-pointer select-none ${on ? 'bg-[#D4A574] text-black font-bold' : 'bg-black/40 text-[#B49B7E]'}`}>
                                                                {/* sr-only rather than hidden: the box stays
                                                                    focusable and announced, so the ticks are
                                                                    reachable by keyboard. */}
                                                                <input type="checkbox" className="sr-only" checked={on}
                                                                  data-testid={`surface-${sf.toLowerCase()}-${it.item_id}`}
                                                                  onChange={() => toggleSurface(it.item_id, sf)} />
                                                                {sf}
                                                              </label>
                                                            );
                                                          })}
                                                        </div>
                                                      ) : (
                                                        <span className="text-[10px] text-gray-600">n/a</span>
                                                      )}
                                                    </td>
                                                  )}
                                                  {extraFields.map(ef => (
                                                    <td key={ef.key} className="p-1">
                                                      <input value={ov[ef.key] || ''} onChange={e => setNote(it.item_id, ef.key, e.target.value)}
                                                        placeholder={ef.placeholder} className={`${inputCls} ${ef.width}`}
                                                        data-testid={`extra-${ef.key}-${it.item_id}`} />
                                                    </td>
                                                  ))}
                                                  <td className="p-1">
                                                    <input value={ov.install_notes || ''} onChange={e => setNote(it.item_id, 'install_notes', e.target.value)}
                                                      placeholder="prints in the notes box" className={inputCls}
                                                      data-testid={`notes-${it.item_id}`} />
                                                  </td>
                                                  <td className="p-1 text-center whitespace-nowrap">
                                                    {!ro && (
                                                    <button onClick={() => toggleExpand(it.item_id)} title="Edit more fields"
                                                      className="text-[#B49B7E] hover:text-[#D4A574] text-xs mr-1"
                                                      data-testid={`expand-${it.item_id}`}>{open ? '▲' : '⋯'}</button>
                                                    )}
                                                    {isIn && (
                                                    <button onClick={() => removeFromSheet(it.item_id)} title="Remove from THIS sheet (stays in FF&E)"
                                                      className="text-red-400/80 hover:text-red-300 text-sm"
                                                      data-testid={`remove-${it.item_id}`}>🗑</button>
                                                    )}
                                                  </td>
                                                </tr>
                                                {open && (
                                                  <tr className="border-b border-[#B49B7E]/10 bg-black/30">
                                                    <td colSpan={colSpan} className="p-2">
                                                      <div className="text-[11px] text-[#B49B7E] mb-2">
                                                        These stay on THIS sheet only — the FF&E master is never changed.
                                                      </div>
                                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                        {[
                                                          ['finish_color', 'Finish / Colour', 'text'],
                                                          ['sku', 'SKU', 'text'],
                                                          ['quantity', 'Quantity', 'number'],
                                                          ['status', 'Status', 'select'],
                                                          ['remarks', 'Remarks', 'text'],
                                                        ].map(([key, label, type]) => (
                                                          <label key={key} className="block">
                                                            <span className="block text-[10px] text-gray-400 mb-0.5">{label}</span>
                                                            {type === 'select' ? (
                                                              <select data-testid={`ffe-${key}-${it.item_id}`} value={it[key] ?? ''}
                                                                onChange={(e) => saveSheetOverride(it.item_id, key, e.target.value)}
                                                                className={inputCls}>
                                                                <option value="">—</option>
                                                                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                                              </select>
                                                            ) : (
                                                              <EditCell testId={`ffe-${key}-${it.item_id}`} value={it[key] ?? ''} type={type}
                                                                onSave={(v) => saveSheetOverride(it.item_id, key, key === 'quantity' ? (parseInt(v, 10) || 1) : v)}
                                                                className={inputCls} placeholder="—" />
                                                            )}
                                                          </label>
                                                        ))}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                              </tbody>
                                            )}
                                          </Draggable>
                                        );
                                      })}
                                      {itemsDrop.placeholder}
                                    </table>
                                  )}
                                </Droppable>
                              </div>
                            ))}
                            {!collapsedRooms[room.room_id] && catFilter !== 'all' && !(room.categories || []).some(c => c.category_name === catFilter) && (
                              <p className="ml-3 mt-1 text-xs text-gray-500 italic">No {catFilter} in this room.</p>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {roomsDrop.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
            </DragDropContext>
          )}
        </div>
      </div>

      {lastBulkAction && (
        <div data-testid="bulk-undo-wrap"
          className="fixed bottom-4 left-4 z-[60] bg-[#1b2436] border border-[#D4A574]/50 rounded-xl shadow-2xl px-3 py-2 flex items-center gap-2">
          <span className="text-xs text-[#B49B7E]">
            {lastBulkAction.type === 'remove'
              ? `Removed ${lastBulkAction.ids.length} from sheet`
              : `Set ${(BULK_FIELDS.find(f => f.key === lastBulkAction.field) || {}).label || lastBulkAction.field} on ${lastBulkAction.ids.length}`}
          </span>
          <button data-testid="bulk-undo" onClick={undoBulk}
            className="px-3 py-1 bg-[#D4A574] hover:bg-[#E2B989] text-black rounded text-xs font-bold">↩ Undo</button>
          <button data-testid="bulk-undo-dismiss" onClick={() => setLastBulkAction(null)}
            className="text-gray-400 hover:text-white text-xs">✕</button>
        </div>
      )}

      {selectedCount > 0 && (
        <div data-testid="bulk-edit-bar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] bg-[#1b2436] border border-[#D4A574]/50 rounded-xl shadow-2xl px-4 py-3 flex items-center gap-2 flex-wrap max-w-[95vw]">
          <span data-testid="bulk-selected-count" className="text-[#D4A574] font-bold text-sm whitespace-nowrap">{selectedCount} selected</span>
          <span className="text-[#B49B7E] text-xs">Set</span>
          <select data-testid="bulk-field" value={bulkField} onChange={e => { setBulkField(e.target.value); setBulkValue(''); }}
            className="bg-black/40 border border-[#D4A574]/30 rounded px-2 py-1 text-white text-xs">
            {BULK_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          {bulkFieldCfg.type === 'select' ? (
            <select data-testid="bulk-value" value={bulkValue} onChange={e => setBulkValue(e.target.value)}
              className="bg-black/40 border border-[#D4A574]/30 rounded px-2 py-1 text-white text-xs min-w-[11rem]">
              <option value="">— choose —</option>
              {(bulkFieldCfg.options || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input data-testid="bulk-value" type={bulkFieldCfg.type === 'number' ? 'number' : 'text'} value={bulkValue}
              onChange={e => setBulkValue(e.target.value)} placeholder={`${bulkFieldCfg.label}…`}
              className="bg-black/40 border border-[#D4A574]/30 rounded px-2 py-1 text-white text-xs w-40" />
          )}
          <button data-testid="bulk-apply" onClick={applyBulk}
            className="px-3 py-1.5 bg-[#D4A574] hover:bg-[#E2B989] text-black rounded text-xs font-bold">Apply to {selectedCount}</button>
          <span className="w-px h-5 bg-[#B49B7E]/30" />
          <button data-testid="bulk-remove" onClick={bulkRemove}
            className="px-3 py-1.5 bg-red-800 hover:bg-red-700 text-white rounded text-xs font-medium">🗑 Remove from sheet</button>
          <button data-testid="bulk-clear" onClick={clearSelection}
            className="px-2 py-1.5 text-gray-300 hover:text-white text-xs">Clear</button>
          <p className="w-full text-[10px] text-[#B49B7E] mt-1">Changes stay on THIS sheet only — the FF&E is never edited from here.</p>
        </div>
      )}

      {attachRoom && (
        <RoomAttachmentsModal
          projectId={projectId}
          roomId={attachRoom.id}
          roomName={attachRoom.name}
          onClose={() => setAttachRoom(null)}
          onChanged={() => loadAttachCounts((sheet?.floors || []).flatMap(f => (f.rooms || []).map(r => r.room_id)))}
        />
      )}

      {photoItem && (
        <ItemPhotosModal
          item={{ id: photoItem.item_id, name: photoItem.name || 'Line item', photos: photoItem.photos || [], image_url: photoItem.image_url || '' }}
          onClose={() => setPhotoItem(null)}
          onSaved={(next) => {
            patchSheetItem(photoItem.item_id, { photos: next });
            // If the item had no main photo yet, show the first one right away.
            if (!photoItem.image_url && next && next.length) {
              patchSheetItem(photoItem.item_id, { image_url: next[0] });
            }
            setPhotoItem((cur) => (cur ? { ...cur, photos: next } : cur));
          }}
          onMainChanged={(src) => {
            onMainPhoto(photoItem.item_id, src);
            setPhotoItem((cur) => (cur ? { ...cur, image_url: src } : cur));
          }}
        />
      )}

      {/* Hidden input used by every room's "Add room header photos" button */}
      <input ref={roomPhotoInputRef} type="file" accept="image/*" multiple className="hidden"
        data-testid="room-photo-file-input"
        onChange={(e) => { addRoomPhotos(roomPhotoTargetRef.current, e.target.files); e.target.value = ''; }} />

      {/* Presets modal */}
      {showPresets && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60]">
          <div className="bg-[#1E293B] border-2 border-[#D4A574] rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="presets-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-[#D4A574]">★ Trade presets</h3>
              <button data-testid="presets-close" onClick={() => setShowPresets(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <p className="text-xs text-gray-400 mb-3">Apply a saved preset to every item on this sheet, or save the current defaults. Presets carry across all projects.</p>
            <div className="space-y-2 mb-4" data-testid="presets-list">
              {presets.length === 0 ? <p className="text-gray-500 text-sm">No presets yet.</p> : presets.map((p) => (
                <div key={p.id} className="flex items-center gap-2 bg-slate-800 rounded px-3 py-2">
                  <span className="flex-1 text-sm text-[#D4C5A9]"><b>{p.name}</b> <span className="text-gray-500 text-xs">{[p.fill_value && `${p.fill_label || p.fill_key}: ${p.fill_value}`, p.install_notes && 'notes'].filter(Boolean).join(' · ')}</span></span>
                  <button data-testid={`preset-apply-${p.id}`} onClick={() => applyPreset(p)} className="px-2 py-1 bg-[#D4A574] text-black rounded text-xs font-bold">Apply</button>
                  <button data-testid={`preset-del-${p.id}`} onClick={() => deletePreset(p.id)} className="px-2 py-1 bg-red-900 text-red-200 rounded text-xs">✕</button>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-700 pt-3 space-y-2">
              <div className="text-sm font-bold text-[#D4C5A9]">Save a new preset</div>
              <input data-testid="preset-name-input" value={presetForm.name} onChange={(e) => setPresetForm({ ...presetForm, name: e.target.value })} placeholder="Preset name (e.g. Standard interior paint)" className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm" />
              {fill && <input data-testid="preset-fill-input" value={presetForm.fill} onChange={(e) => setPresetForm({ ...presetForm, fill: e.target.value })} placeholder={`${fill.label} value — applied to every item`} className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm" />}
              <textarea data-testid="preset-notes-input" value={presetForm.notes} onChange={(e) => setPresetForm({ ...presetForm, notes: e.target.value })} placeholder="Installation notes (optional)" className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm min-h-[52px]" />
              <button data-testid="preset-save-btn" onClick={savePreset} className="px-3 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded text-sm font-bold">💾 Save preset</button>
            </div>
          </div>
        </div>
      )}

      {/* Templates modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60]">
          <div className="bg-[#1E293B] border-2 border-[#D4A574] rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="templates-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-[#D4A574]">📐 Sheet templates</h3>
              <button data-testid="templates-close" onClick={() => setShowTemplates(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <p className="text-xs text-gray-400 mb-3">A template remembers this sheet's usual sources + fill defaults, so you can stamp it onto a brand-new home in one click.</p>
            <div className="space-y-2 mb-4" data-testid="templates-list">
              {templates.length === 0 ? <p className="text-gray-500 text-sm">No templates yet.</p> : templates.map((t) => (
                <div key={t.id} className="flex items-center gap-2 bg-slate-800 rounded px-3 py-2">
                  <span className="flex-1 text-sm text-[#D4C5A9]"><b>{t.is_default ? '★ ' : ''}{t.name}</b> <span className="text-gray-500 text-xs">{[(t.wh_categories || []).length && `sources: ${t.wh_categories.join(', ')}`, t.fill_value && `${t.fill_key}: ${t.fill_value}`, t.room_photos && Object.keys(t.room_photos).length && 'room photos'].filter(Boolean).join(' · ')}</span></span>
                  {t.is_default
                    ? <span className="px-2 py-1 bg-amber-200 text-amber-900 rounded text-xs font-bold">★ Default</span>
                    : <button data-testid={`template-default-${t.id}`} onClick={() => setDefaultTemplate(t.id)} className="px-2 py-1 bg-slate-600 hover:bg-slate-500 text-white rounded text-xs">Set default</button>}
                  <button data-testid={`template-apply-${t.id}`} onClick={() => applyTemplate(t)} className="px-2 py-1 bg-[#D4A574] text-black rounded text-xs font-bold">Apply</button>
                  <button data-testid={`template-del-${t.id}`} onClick={() => deleteTemplate(t.id)} className="px-2 py-1 bg-red-900 text-red-200 rounded text-xs">✕</button>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-700 pt-3 space-y-2">
              <div className="text-sm font-bold text-[#D4C5A9]">Save THIS sheet's setup as a template</div>
              <input data-testid="template-name-input" value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Template name (e.g. Standard 3-bed build)" className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm" />
              <button data-testid="template-save-btn" onClick={saveTemplate} className="px-3 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded text-sm font-bold">💾 Save as template</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Modal (3 sources) */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60]">
          <div className="bg-[#1E293B] border-2 border-[#D4A574] rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#D4A574]">✚ Add Item</h3>
              <button data-testid="add-item-close" onClick={closeAdd} className="text-gray-400 hover:text-white">✕</button>
            </div>
            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              {[['custom', '✍️ Write-in'], ['ffe', '📦 From FF&E'], ['checklist', '📋 From Checklist']].map(([k, lbl]) => (
                <button key={k} data-testid={`add-item-tab-${k}`} onClick={() => openAdd(k)} className={`px-3 py-1.5 rounded text-sm ${addTab === k ? 'bg-[#D4A574] text-black font-bold' : 'bg-slate-700 text-gray-200'}`}>{lbl}</button>
              ))}
            </div>

            {addTab === 'custom' && (
              <div className="space-y-3">
                <p className="text-xs text-[#B49B7E]">Creates the item back in the FF&E under the room + category you choose.</p>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Room *</label>
                  <select data-testid="add-item-room" value={addForm.room_id} onChange={e => setAddForm(f => ({ ...f, room_id: e.target.value, category_id: '' }))} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm">
                    <option value="">Select room…</option>
                    {pickerRooms.map(r => <option key={r.room_id} value={r.room_id}>{r.floor} — {r.room_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Category *</label>
                  <select data-testid="add-item-category" value={addForm.category_id} onChange={e => setAddForm(f => ({ ...f, category_id: e.target.value }))} disabled={!addForm.room_id} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm disabled:opacity-50">
                    <option value="">Select category…</option>
                    {pickerCats.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
                  </select>
                </div>
                <input data-testid="add-item-name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} placeholder="Item name *" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" />
                <input data-testid="add-item-vendor" value={addForm.vendor} onChange={e => setAddForm(f => ({ ...f, vendor: e.target.value }))} placeholder="Vendor" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" />
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="add-item-size" value={addForm.size} onChange={e => setAddForm(f => ({ ...f, size: e.target.value }))} placeholder="Size" className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" />
                  <input data-testid="add-item-shape" value={addForm.shape_note} onChange={e => setAddForm(f => ({ ...f, shape_note: e.target.value }))} placeholder="Shape note" className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Photo (optional)</label>
                  <input data-testid="add-item-photo" type="file" accept="image/*" onChange={e => onImage(e.target.files?.[0])} className="w-full text-xs text-gray-300" />
                  {addForm.image_url && <img src={addForm.image_url} alt="" className="mt-2 w-16 h-16 object-cover rounded" />}
                </div>
                <div className="flex gap-3 pt-2">
                  <button data-testid="add-item-cancel" onClick={closeAdd} className="flex-1 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded text-sm">Cancel</button>
                  <button data-testid="add-item-submit" onClick={submitCustom} className="flex-1 py-2 bg-[#D4A574] hover:bg-[#E2B989] text-black rounded font-bold text-sm">Add to Sheet &amp; FF&E</button>
                </div>
              </div>
            )}

            {addTab === 'ffe' && (
              <div>
                <input data-testid="add-ffe-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search FF&E items not on the sheet…" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm mb-3" />
                {sortControl}
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {ffeCandidates.length === 0 ? (
                    <p className="text-gray-500 text-sm p-2">All FF&E items are already on the sheet.</p>
                  ) : (ffeCandidates || []).map(it => (
                    <div key={it.item_id} className="flex items-center justify-between gap-2 bg-black/30 rounded p-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {pickerThumb(it.image_url, it.name)}
                        <div className="text-sm text-[#D4C5A9] min-w-0">
                          <div className="truncate">{it.name}</div>
                          <div className="text-xs text-gray-500 truncate">{it.room_name} / {it.category_name}{it.vendor ? ` · ${it.vendor}` : ''}</div>
                        </div>
                      </div>
                      <button data-testid={`add-ffe-${it.item_id}`} onClick={() => setItemStates(prev => ({ ...prev, [it.item_id]: true }))} className="px-2 py-1 bg-[#D4A574] text-black rounded text-xs font-bold flex-shrink-0">Add</button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Added items turn ON in the list. Changes save automatically.</p>
              </div>
            )}

            {addTab === 'checklist' && (
              <div>
                <input data-testid="add-checklist-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search Checklist items…" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm mb-3" />
                {sortControl}
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {checklistItems === null ? (
                    <p className="text-gray-500 text-sm p-2">Loading checklist…</p>
                  ) : checklistCandidates.length === 0 ? (
                    <p className="text-gray-500 text-sm p-2">No checklist items found.</p>
                  ) : (checklistCandidates || []).map((ci, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 bg-black/30 rounded p-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {pickerThumb(ci.image_url, ci.name)}
                        <div className="text-sm text-[#D4C5A9] min-w-0">
                          <div className="truncate">{ci.name}</div>
                          <div className="text-xs text-gray-500 truncate">{ci.room_name} / {ci.category_name}{ci.vendor ? ` · ${ci.vendor}` : ''}</div>
                        </div>
                      </div>
                      <button data-testid={`use-checklist-${i}`} onClick={() => pickChecklistItem(ci)} className="px-2 py-1 bg-[#D4A574] text-black rounded text-xs font-bold">Use</button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">&quot;Use&quot; pre-fills the Write-in form; pick the FF&E room + category, then add (creates it in FF&E).</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
